import type { FormEvent, ReactNode } from 'react';
import { useState, useCallback } from 'react';
import { Modal } from '@simplywallst/ui-core';
import { Textfield, Grid } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';

type Props = {
  title?: string;
  onClose: () => void;
  onConfirm: (viewName: string, description: string) => void;
  oldName?: string;
  oldDescription?: string | null;
  confirmLabel?: string;
  secondaryLabel?: string;
  primaryLoading?: boolean;
  disabled?: boolean;
  children?: ReactNode;
};

export const EditAndSaveScreenerModal = ({
  title,
  onClose,
  onConfirm,
  oldName,
  oldDescription,
  confirmLabel,
  secondaryLabel,
  primaryLoading,
  disabled,
  children,
}: Props) => {
  const [viewName, setViewName] = useState(oldName ?? '');
  const [viewDescription, setViewDescription] = useState(oldDescription ?? '');
  const [showViewNameError, setShowViewNameError] = useState(false);
  const { Track } = useTracking({
    type: 'track',
    subject: 'EditAndSaveScreenerModal',
  });

  const handleConfirm = useCallback(() => {
    setShowViewNameError(viewName.length === 0);

    if (!showViewNameError) {
      onConfirm(viewName, viewDescription);
    }
  }, [onConfirm, showViewNameError, viewName, viewDescription]);

  return (
    <Track>
      <Modal
        title={title}
        onSecondaryClick={onClose}
        onPrimaryClick={handleConfirm}
        primaryLabel={confirmLabel}
        secondaryLabel={secondaryLabel}
        primaryLoading={primaryLoading}
        disabled={disabled}
      >
        <Grid autoRows="max-content 1fr" gap="16px" height="100%">
          <Grid autoRows="max-content" gap="16px">
            <Textfield
              name="viewName"
              styleType="standardReverse"
              type="text"
              label="Name"
              placeholder="e.g. High Growth Stocks"
              value={viewName}
              onChange={(evt: FormEvent<HTMLInputElement>) => {
                setViewName(evt.currentTarget.value);
                setShowViewNameError(evt.currentTarget.value.length === 0); // can be derived
              }}
              error={showViewNameError ? 'Name cannot be blank' : ''}
            />
            <Textfield
              name="viewDescription"
              styleType="standardReverse"
              type="text"
              label="Description (optional)"
              placeholder="Describe your screener"
              value={viewDescription}
              onChange={(evt: FormEvent<HTMLInputElement>) => {
                setViewDescription(evt.currentTarget.value);
              }}
            />
          </Grid>
          <Grid>{children}</Grid>
        </Grid>
      </Modal>
    </Track>
  );
};
